import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Box,
  Button,
  Icon,
  ScrollArea,
  Text,
} from "@salestrak/ui";
import { LoadErrorState } from "#app/components/settings/load-error-state";
import { PageHeader, PageHeaderSubtitle, PageHeaderTitle, SETTINGS_CONTENT_MAX_WIDTH, SettingsPageGutter } from "#app/components/page-header";
import {
  useApiKeys,
  useApiKeyMutations,
  type ApiKeyItem,
} from "#app/hooks/settings/use-api-key-mutations";
import {
  useWebhooks,
  useWebhookMutations,
  type WebhookItem,
} from "#app/hooks/settings/use-webhook-mutations";
import {
  SettingsGroup,
  SettingsGroupEmptyRow,
  SettingsGroupHeader,
  SettingsGroupRow,
  SettingsGroupRowMeta,
} from "#app/components/settings/settings-group";
import { SettingsGroupRowsSkeleton } from "#app/components/settings/settings-page-skeleton";
import { WebhookActionsMenu } from "#app/components/settings/webhook-actions-menu";
import { ApiKeyActionsMenu } from "#app/components/settings/api-key-actions-menu";
import { EntityTag } from "#app/components/entity";
import { getPermissionsLabel } from "#app/components/settings/permission-badge";
import { formatDate } from "#app/lib/utils/format-date";
import { TrashConfirmDialog } from "#app/components/settings/trash-confirm-dialog";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { withPermission } from "#app/components/permissions/require-permission";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import {
  displayUrl,
  pinnedStageId,
  targetLabel,
  triggerLabel,
  webhookTargetNames,
} from "#app/components/settings/webhook-options";

function ApiPage() {
  const { push } = useAppNavigate();

  const {
    webhooks,
    total: webhookTotal,
    isLoading: webhooksLoading,
    error: webhooksError,
    refetch: refetchWebhooks,
  } = useWebhooks();
  const { deleteWebhook, toggleWebhookArchived } = useWebhookMutations();
  const {
    apiKeys,
    total: apiKeyTotal,
    isLoading: apiKeysLoading,
    error: apiKeysError,
    refetch: refetchApiKeys,
  } = useApiKeys();
  const { revokeApiKey } = useApiKeyMutations();
  const { stageMap, workflowMap } = useWorkspaceData();

  const [deletingWebhook, setDeletingWebhook] = useState<WebhookItem | null>(null);
  const [revokingApiKey, setRevokingApiKey] = useState<{ apiKeyId: string; name: string } | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const error = webhooksError || apiKeysError;

  async function handleDeleteWebhook() {
    if (!deletingWebhook) return;
    await deleteWebhook(deletingWebhook.webhookId);
  }

  async function handleRevokeApiKey() {
    if (!revokingApiKey) return;
    await revokeApiKey(revokingApiKey.apiKeyId);
  }

  // No whole-page loading gate: the header is static, so it renders for real
  // while each list reserves one skeleton row in place.
  if (error && webhooks.length === 0 && apiKeys.length === 0) {
    return (
      <LoadErrorState
        noun="API settings"
        error={error}
        onRetry={() => {
          void refetchWebhooks();
          void refetchApiKeys();
        }}
      />
    );
  }

  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderTitle>API</PageHeaderTitle>
          <PageHeaderSubtitle>
            Build integrations and automations on the SalesTrak GraphQL API.
          </PageHeaderSubtitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box gap="2xl" fill="width" maxWidth={SETTINGS_CONTENT_MAX_WIDTH}>
            {/* Webhooks */}
            <SettingsGroup
              label="Webhooks"
              subtitle="Receive an HTTP request when a Project is created, updated or deleted"
            >
              {webhooks.length > 0 ? (
                <SettingsGroupHeader>
                  <Text size="sm" weight="medium">
                    {webhookTotal} {webhookTotal === 1 ? "webhook" : "webhooks"}
                  </Text>
                  <Button
                    aria-label="Create webhook"
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => push("/settings/api/webhooks/create")}
                  >
                    <Icon name="plus" size="md" />
                  </Button>
                </SettingsGroupHeader>
              ) : null}

              {webhooksLoading && webhooks.length === 0 ? (
                <SettingsGroupRowsSkeleton rows={1} />
              ) : null}

              {!webhooksLoading && webhooks.length === 0 ? (
                <SettingsGroupEmptyRow
                  label="No webhooks"
                  actionLabel="New webhook"
                  onAction={() => push("/settings/api/webhooks/create")}
                />
              ) : null}

              {webhooks.map((webhook: WebhookItem) => {
                // The row tells the story: state, what fires it, where it
                // points. The URL closes the line and takes the ellipsis.
                const target = targetLabel(
                  webhookTargetNames(pinnedStageId(webhook.when), stageMap, workflowMap),
                );
                const detail = [
                  triggerLabel(webhook.resource, webhook.trigger),
                  target,
                  displayUrl(webhook.url),
                ]
                  .filter(Boolean)
                  .join(" · ");
                return (
                <SettingsGroupRow
                  key={webhook.webhookId}
                  label={webhook.name}
                  subtitle={
                    <SettingsGroupRowMeta
                      state={webhook.archived ? "Disabled" : "Enabled"}
                      tone={webhook.archived ? "muted" : "success"}
                      detail={detail}
                    />
                  }
                  icon="webhook"
                  // The hover "…" menu is the row's trailing affordance; a
                  // chevron beside it doubled the furniture.
                  chevron={false}
                  onClick={() => push(`/settings/api/webhooks/${webhook.webhookId}`)}
                  actionsVisible={openMenuId === webhook.webhookId}
                >
                  <WebhookActionsMenu
                    archived={webhook.archived}
                    onOpenChange={(open) => setOpenMenuId(open ? webhook.webhookId : null)}
                    onEdit={() => push(`/settings/api/webhooks/${webhook.webhookId}/edit`)}
                    onToggleArchived={() => toggleWebhookArchived(webhook)}
                    onDelete={() => setDeletingWebhook(webhook)}
                  />
                </SettingsGroupRow>
                );
              })}
            </SettingsGroup>

            {/* Member API Keys */}
            <SettingsGroup
              label="Member API Keys"
              subtitle="Members create personal keys for the SalesTrak API"
            >
              {apiKeys.length > 0 ? (
                <SettingsGroupHeader>
                  <Text size="sm" weight="medium">
                    {apiKeyTotal} {apiKeyTotal === 1 ? "API key" : "API keys"}
                  </Text>
                  <Button
                    aria-label="Create API key"
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => push("/settings/api/keys/create")}
                  >
                    <Icon name="plus" size="md" />
                  </Button>
                </SettingsGroupHeader>
              ) : null}

              {apiKeysLoading && apiKeys.length === 0 ? (
                <SettingsGroupRowsSkeleton rows={1} />
              ) : null}

              {!apiKeysLoading && apiKeys.length === 0 ? (
                <SettingsGroupEmptyRow
                  label="No API keys"
                  actionLabel="New API key"
                  onAction={() => push("/settings/api/keys/create")}
                />
              ) : null}

              {apiKeys.map((key: ApiKeyItem) => {
                const dateLabel = key.creationDate ? formatDate(key.creationDate) : null;
                return (
                  <SettingsGroupRow
                    key={key.apiKeyId}
                    label={key.name}
                    subtitle={`${getPermissionsLabel(key.permissions)}${dateLabel ? ` · ${dateLabel}` : ""}`}
                    leading={
                      <EntityTag
                        entity={{
                          kind: "user",
                          id: key.user?.userId ?? key.apiKeyId,
                          user: key.user ?? null,
                        }}
                        display="visual"
                        size="md"
                        interactive={false}
                      />
                    }
                    chevron={false}
                    onClick={() => push(`/settings/api/keys/${key.apiKeyId}`)}
                    actionsVisible={openMenuId === key.apiKeyId}
                  >
                    <ApiKeyActionsMenu
                      onOpenChange={(open) => setOpenMenuId(open ? key.apiKeyId : null)}
                      onEdit={() => push(`/settings/api/keys/${key.apiKeyId}/edit`)}
                      onRevoke={() => setRevokingApiKey({ apiKeyId: key.apiKeyId, name: key.name })}
                    />
                  </SettingsGroupRow>
                );
              })}
            </SettingsGroup>

            <SettingsPageGutter />
          </Box>
        </Box>
      </ScrollArea>

      <TrashConfirmDialog
        open={deletingWebhook !== null}
        onOpenChange={(open) => {
          if (!open) setDeletingWebhook(null);
        }}
        title="Delete webhook?"
        description={`This will permanently delete the webhook for ${deletingWebhook?.url ?? ""}. This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDeleteWebhook}
      />

      <TrashConfirmDialog
        open={revokingApiKey !== null}
        onOpenChange={(open) => {
          if (!open) setRevokingApiKey(null);
        }}
        title="Revoke API key?"
        description={`This will permanently revoke "${revokingApiKey?.name ?? ""}". Any applications using this key will lose access immediately.`}
        confirmLabel="Revoke"
        onConfirm={handleRevokeApiKey}
      />
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/api/")({
  component: withPermission("workspace.integrations", ApiPage),
});
